/**
 * APM Anomaly Detection API Service
 * Frontend service for statistical anomaly detection and AI analysis
 */

import apiClient from './api.service';

// Types
export interface AnomalyConfig {
  tenantId: string;
  applicationId?: string;
  enabled: boolean;
  useZScore: boolean;
  zScoreThreshold: number;
  useMovingAverage: boolean;
  movingAverageWindow: number;
  movingAverageThreshold: number;
  usePercentile: boolean;
  percentileThreshold: number;
  useEWMA: boolean;
  ewmaAlpha: number;
  ewmaThreshold: number;
  monitorResponseTime: boolean;
  monitorErrorRate: boolean;
  monitorThroughput: boolean;
  monitorLatencyP99: boolean;
  minDataPoints: number;
  cooldownMinutes: number;
  autoThresholds: boolean;
  baselinePeriodDays: number;
}

export interface DetectedAnomaly {
  id: string;
  timestamp: string;
  type: 'response_time' | 'error_rate' | 'throughput' | 'latency_p99' | 'slow_query' | 'error_spike';
  metric: string;
  currentValue: number;
  expectedValue: number;
  deviation: number;
  deviationPercent: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  detectionMethod: 'zscore' | 'moving_avg' | 'percentile' | 'ewma' | 'threshold';
  affectedService: string;
  applicationId: string;
  description: string;
  context?: {
    baseline?: {
      mean: number;
      stdDev: number;
      p50: number;
      p95: number;
      p99: number;
    };
    recentTrend?: 'increasing' | 'decreasing' | 'stable';
  };
  aiAnalysis?: AnomalyAIAnalysis;
}

export interface AnomalyAIAnalysis {
  summary: string;
  rootCause: string;
  impact: string;
  recommendations: string[];
  confidenceScore: number;
  severity: string;
  analyzedAt?: string;
}

export interface AnomalyDetectionResult {
  tenantId: string;
  applicationId?: string;
  timestamp: string;
  duration: number;
  anomaliesDetected: number;
  anomalies: DetectedAnomaly[];
  metricsAnalyzed: number;
  alertsCreated: number;
  baselineQuality: 'good' | 'limited' | 'insufficient';
}

export interface AnomalyStatistics {
  totalAnomalies: number;
  bySeverity: Record<string, number>;
  byType: Record<string, number>;
  trend: Array<{ date: string; count: number }>;
  timeRange: {
    start: string;
    end: string;
  };
}

// API Functions
export const anomalyApi = {
  /**
   * Get anomaly detection configuration
   */
  async getConfig(applicationId?: string): Promise<AnomalyConfig> {
    const params: Record<string, any> = {};
    if (applicationId) params.applicationId = applicationId;
    const result = await apiClient.get('/v1/apm/anomaly/config', { params });
    return result.data;
  },

  /**
   * Update anomaly detection configuration
   */
  async updateConfig(config: Partial<AnomalyConfig>): Promise<AnomalyConfig> {
    const result = await apiClient.put('/v1/apm/anomaly/config', config);
    return result.data;
  },

  /**
   * Run anomaly detection manually
   */
  async runDetection(applicationId?: string): Promise<AnomalyDetectionResult> {
    const result = await apiClient.post('/v1/apm/anomaly/detect', { applicationId });
    return result.data;
  },

  /**
   * Get recent anomalies
   */
  async getAnomalies(params?: {
    applicationId?: string;
    limit?: number;
    severity?: string;
    type?: string;
  }): Promise<{ data: DetectedAnomaly[]; meta: { total: number; limit: number } }> {
    return apiClient.get('/v1/apm/anomaly/anomalies', { params });
  },

  /**
   * Get anomaly statistics
   */
  async getStatistics(params?: {
    applicationId?: string;
    startTime?: number;
    endTime?: number;
  }): Promise<AnomalyStatistics> {
    const result = await apiClient.get('/v1/apm/anomaly/statistics', { params });
    return result.data;
  },

  /**
   * Get AI analysis for a specific anomaly
   */
  async analyzeAnomaly(anomalyId: string): Promise<{
    anomalyId: string;
    analysis: AnomalyAIAnalysis;
  }> {
    const result = await apiClient.post(`/v1/apm/anomaly/analyze/${anomalyId}`);
    return result.data;
  },
};

export default anomalyApi;
